import React from "react";
import { Card } from "@/components/game/card";
import { cn } from "@/lib/utils";
import { useResponsive } from "@/lib/responsive";
import { useGameResponsive } from "@/hooks/use-game-responsive";
import { playGameSound } from "@/utils/sound-utils";

export interface CardSlotsProps {
  cards: string[];
  isOpponent?: boolean;
  opponentId?: number;
}

export const CardSlots: React.FC<CardSlotsProps> = ({
  cards,
  isOpponent = false,
  opponentId,
}) => {
  // Use both hooks for backward compatibility during transition
  const { getEmptySlotSize, getFilledSlotSize, screenSize: oldScreenSize } = useResponsive();
  const {
    screenSize,
    dimensions,
    isLandscape,
    isPortrait,
    isMobileDevice: isMobile,
    isTabletDevice: isTablet,
    getCardSize,
    getSpacing,
    getAnimationSpeed,
    getOptimalCardSize,
  } = useGameResponsive();

  const spacing = getSpacing();
  const gapClass = `gap-${Math.min(Math.max(Math.floor(spacing/4), 1), 6)}`;
  const cardAnimation = isOpponent ? 'animate-fadeInDown' : 'animate-fadeInUp';

  return (
    <div
      className={cn(
        "flex justify-center items-center transition-all duration-300 relative",
        gapClass,
        isOpponent ? "opacity-90" : "opacity-100"
      )}
      style={{
        gap: isLandscape ? `${Math.max(dimensions.width * 0.015, 8)}px` : `${Math.max(dimensions.height * 0.02, 8)}px`,
      }}
    >
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={`${isOpponent ? "opponent" : "player"}-slot-${index}`}
          className="relative"
        >
          {!cards[index] && (
            <div
              className={cn(
                isPortrait ?
                  (isMobile ? "w-[50px] h-[90px]" : isTablet ? "w-[60px] h-[100px]" : "w-[80px] h-[120px]") :
                  (isMobile ? "w-[60px] h-[100px]" : isTablet ? "w-[80px] h-[120px]" : "w-[100px] h-[150px]"),
                "flex items-center justify-center transition-all duration-300"
              )}
            >
              <div
                className={cn(
                  "rounded-full border border-dashed border-white/10 animate-pulse opacity-30",
                  isMobile ? "w-5 h-5" : (isTablet ? "w-6 h-6" : "w-7 h-7")
                )}
              ></div>
            </div>
          )}
          {cards[index] && (
            <Card
              value={cards[index]}
              className={cn(
                isPortrait ?
                  (isMobile ? "w-[60px] h-[100px]" : isTablet ? "w-[70px] h-[120px]" : "w-[90px] h-[140px]") :
                  (isMobile ? "w-[70px] h-[110px]" : isTablet ? "w-[90px] h-[140px]" : "w-[110px] h-[170px]"),
                cardAnimation,
                "transition-all duration-300"
              )}
              onClick={() => playGameSound("gameplay.cardPlace")}
              noAnimation={true}
              isOpponent={isOpponent}
              opponentId={opponentId}
            />
          )}
        </div>
      ))}
    </div>
  );
};
